import { Money } from '@palaccount/money';

/**
 * Wave 4A — the client-side arithmetic of applying a credit note to
 * invoices. The SERVER is the authority (its caps are re-read under row
 * locks); these two functions only PREFILL the dialog and colour its strip
 * between keystrokes, the same division as `receipts/allocation.ts`.
 *
 * Nothing here is ever a JS number.
 */

/** One open invoice as the dialog lists it. */
export interface OpenInvoiceRow {
  invoiceId: string;
  balanceDue: string;
}

/** N-13 / BR-FIN-03-15 — every money comparison is within half a cent. */
const HALF_CENT = '0.005';
const AMOUNT_RE = /^\d+(\.\d+)?$/;

/**
 * BR-FIN-03-10's walk, for the prefill: the invoices OLDEST FIRST (the
 * caller passes them in that order — it is the list's order, not this
 * function's job), each taking the smaller of its balance and the credit
 * still unspent, until the credit runs out. Invoices the credit never
 * reaches get no cell at all, so the dialog shows them blank rather than
 * as "0.00".
 */
export function prefillOldestFirst(
  invoices: readonly OpenInvoiceRow[],
  available: string,
  currency: string,
): Record<string, string> {
  const out: Record<string, string> = {};
  let remaining = Money.of(available, currency);
  for (const invoice of invoices) {
    if (!remaining.isPositive()) break;
    const balance = Money.of(invoice.balanceDue, currency);
    if (!balance.isPositive()) continue;
    const applied = balance.lessThan(remaining) ? balance : remaining;
    out[invoice.invoiceId] = applied.toFixed2();
    remaining = remaining.minus(applied);
  }
  return out;
}

export interface ApplyView {
  /** Σ of the well-formed, positive cells. */
  total: string;
  /** `available − total`; negative when over. */
  remaining: string;
  /** Σ exceeds the unapplied credit by more than half a cent. */
  over: boolean;
  /** Cells applying more than their invoice's balance (the server's per-invoice cap, drawn early). */
  overRows: ReadonlySet<string>;
  /** The rows the request would carry: positive cells only. */
  allocations: { invoiceId: string; amount: string }[];
}

/** The dialog's strip and payload, from every cell it holds. */
export function computeApplyView(
  amounts: Readonly<Record<string, string>>,
  invoices: readonly OpenInvoiceRow[],
  available: string,
  currency: string,
): ApplyView {
  const halfCent = Money.of(HALF_CENT, currency);
  const balances = new Map(invoices.map((invoice) => [invoice.invoiceId, invoice.balanceDue]));
  let total = Money.zero(currency);
  const overRows = new Set<string>();
  const allocations: { invoiceId: string; amount: string }[] = [];
  for (const [invoiceId, raw] of Object.entries(amounts)) {
    const text = raw.trim();
    if (!AMOUNT_RE.test(text)) continue;
    const amount = Money.of(text, currency);
    if (!amount.isPositive()) continue;
    total = total.plus(amount);
    allocations.push({ invoiceId, amount: amount.toFixed2() });
    const balance = balances.get(invoiceId);
    if (balance !== undefined && amount.minus(Money.of(balance, currency)).greaterThan(halfCent)) {
      overRows.add(invoiceId);
    }
  }
  const remaining = Money.of(available, currency).minus(total);
  return {
    total: total.toFixed2(),
    remaining: remaining.toFixed2(),
    over: total.minus(Money.of(available, currency)).greaterThan(halfCent),
    overRows,
    allocations,
  };
}
